import React, { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Plus, LayoutGrid } from 'lucide-react';
import ShotCardList from './ShotCardList';

const VirtualShotList = ({
  rows,
  listViewProps,
  onInsertAtTop,
  onShowGridWizard,
  isGlobalLocked,
  theme,
}) => {
  const parentRef = useRef(null);
  const getRowKey = (scene, index) => scene?.__clientKey || scene?.id || index;

  // 大数据量使用虚拟滚动 - 必须放在条件判断之前，保证 hooks 顺序一致
  const virtualizer = useVirtualizer({
    count: rows?.length || 0,
    getScrollElement: () => parentRef.current,
    getItemKey: (index) => getRowKey(rows?.[index], index),
    estimateSize: () => 450, // 增加预估高度，接近真实卡片高度
    overscan: 8, // ✅ 提高预渲染数量，防止白屏
    gap: 16, // 使用虚拟列表 gap 控制卡片间距
  });

  // 如果数据量小，使用普通渲染（不使用虚拟滚动）
  if (!rows || rows.length < 30) {
    return (
      <div ref={parentRef} className="absolute inset-0 overflow-y-auto custom-scrollbar px-5 py-3 pb-32">
        <div className="space-y-3">
          {/* Grid Workflow Button */}
          <div
            onClick={onShowGridWizard}
            className="w-full h-8 rounded-md flex items-center justify-center cursor-pointer transition-all border gap-2"
            style={{
              backgroundColor: `${theme.colors.bluePrimary}15`,
              borderColor: `${theme.colors.bluePrimary}40`,
              color: theme.colors.bluePrimary,
            }}
          >
            <LayoutGrid size={14} />
            <span className="text-xs font-bold uppercase tracking-wider">
              一致性角色向导 (Grid Workflow)
            </span>
          </div>

          {/* Top Insert Button */}
          <div
            onClick={() => !isGlobalLocked && onInsertAtTop()}
            className="w-full h-8 border border-dashed rounded-md flex items-center justify-center transition-all group cursor-pointer gap-2"
            style={{
              borderColor: 'var(--color-border-primary)',
              backgroundColor: 'var(--color-bg-secondary)',
              color: 'var(--color-text-muted)',
              opacity: isGlobalLocked ? 0.5 : 1,
              cursor: isGlobalLocked ? 'not-allowed' : 'pointer',
            }}
          >
            {isGlobalLocked ? (
              <span className="text-xs font-bold uppercase tracking-wider">
                🔒 任务进行中，锁定添加
              </span>
            ) : (
              <>
                <Plus size={14} className="group-hover:scale-110 transition-transform" />
                <span className="text-xs font-bold uppercase tracking-wider">
                  顶部插入镜头
                </span>
              </>
            )}
          </div>

          {/* Shot Cards */}
          <div className="space-y-4">
            {rows && rows.map((scene, index) => (
              <ShotCardList
                key={getRowKey(scene, index)}
                index={index}
                scene={scene}
                {...listViewProps}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // 大数据量使用虚拟滚动结果
  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div
      ref={parentRef}
      className="absolute inset-0 overflow-y-auto custom-scrollbar px-5 py-3 pb-32"
         >
      {/* 头部区域 */}
      <div className="space-y-3 mb-4">
        {/* Grid Workflow Button */}
        <div
          onClick={onShowGridWizard}
          className="w-full h-8 rounded-md flex items-center justify-center cursor-pointer transition-all border gap-2"
          style={{
            backgroundColor: `${theme.colors.bluePrimary}15`,
            borderColor: `${theme.colors.bluePrimary}40`,
            color: theme.colors.bluePrimary,
          }}
        >
          <LayoutGrid size={14} />
          <span className="text-xs font-bold uppercase tracking-wider">
            一致性角色向导 (Grid Workflow)
          </span>
        </div>

        {/* Top Insert Button */}
        <div
          onClick={() => !isGlobalLocked && onInsertAtTop()}
          className="w-full h-8 border border-dashed rounded-md flex items-center justify-center transition-all group cursor-pointer gap-2"
          style={{
            borderColor: 'var(--color-border-primary)',
            backgroundColor: 'var(--color-bg-secondary)',
            color: 'var(--color-text-muted)',
            opacity: isGlobalLocked ? 0.5 : 1,
            cursor: isGlobalLocked ? 'not-allowed' : 'pointer',
          }}
        >
          {isGlobalLocked ? (
            <span className="text-xs font-bold uppercase tracking-wider">
              🔒 任务进行中，锁定添加
            </span>
          ) : (
            <>
              <Plus size={14} className="group-hover:scale-110 transition-transform" />
              <span className="text-xs font-bold uppercase tracking-wider">
                顶部插入镜头
              </span>
            </>
          )}
        </div>
      </div>

      {/* 虚拟列表区域 */}
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualItem) => {
          const index = virtualItem.index;
          const scene = rows[index];

          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
              }}
              // ✅ 移除 mb-6，由 gap 控制间距
            >
              <ShotCardList
                index={index}
                scene={scene}
                {...listViewProps}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VirtualShotList;
